import React, { useEffect, useState } from "react";
import { TrendUp, TrendDown } from "@phosphor-icons/react";
import { Sparklines, SparkLine } from "@/components/common/Sparkline";

function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const num = Number(target) || 0;
    const step = (t) => {
      const p = Math.min((t - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(num * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function fmt(v, prefix, suffix) {
  let display;
  if (v >= 1000000) display = (v / 1000000).toFixed(1) + "M";
  else if (v >= 1000) display = (v / 1000).toFixed(1) + "K";
  else display = Number.isInteger(v) ? v.toString() : v.toFixed(2);
  return `${prefix || ""}${display}${suffix || ""}`;
}

export function KpiCard({ label, value, prefix, suffix, trend, color, spark, delay = 0 }) {
  const animated = useCountUp(value);
  const up = (trend || 0) >= 0;
  return (
    <div
      data-testid={`kpi-card-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className="group relative overflow-hidden rounded-2xl border border-border bg-surface-raised p-6 transition-transform duration-200 ease-out hover:-translate-y-1"
      style={{ animation: `fade-up 0.5s ease-out ${delay}s both` }}
    >
      <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full opacity-20 blur-2xl" style={{ background: color }} />
      <div className="flex items-start justify-between">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
        {trend != null && (
          <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${up ? "bg-lime-500/15 text-lime-400" : "bg-pink-500/15 text-pink-400"}`}>
            {up ? <TrendUp size={12} weight="bold" /> : <TrendDown size={12} weight="bold" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="mt-3 font-display text-3xl font-black tracking-tight">{fmt(animated, prefix, suffix)}</p>
      {spark && (
        <div className="mt-3 h-8">
          <Sparklines data={spark}>
            <SparkLine color={color} />
          </Sparklines>
        </div>
      )}
    </div>
  );
}
